import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptLanguageHeader, useI18n } from '../i18n';
import {
  NOTIFICATION_LANGUAGES,
  NOTIFICATION_STATUSES,
  REWARD_PLACEHOLDERS,
} from '../constants/notificationTemplates';

const getDefaultTemplates = () => {
  const result = {};
  NOTIFICATION_LANGUAGES.forEach((language) => {
    result[language.code] = {};
    NOTIFICATION_STATUSES.forEach((status) => {
      result[language.code][status.key] = {
        title: '',
        body: '',
        enabled: true,
      };
    });
  });
  return result;
};

export default function PushTemplatesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState(getDefaultTemplates);
  const [changedTemplates, setChangedTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const languageIds = useRef({});
  const statusIds = useRef({});

  useEffect(() => {
    if (!user?.api_token) return;

    const loadTemplates = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch('/api/push-templates', {
          headers: {
            Authorization: `Bearer ${user.api_token}`,
            'Accept-Language': getAcceptLanguageHeader(),
          },
        });
        if (!response.ok) {
          throw new Error(t('Ошибка загрузки push шаблонов'));
        }

        const data = await response.json();
        const rows = Array.isArray(data.templates) ? data.templates : data;
        const parsed = getDefaultTemplates();
        const languageMap = {};
        const statusMap = {};

        rows.forEach((language) => {
          if (!parsed[language.language_code]) return;
          languageMap[language.language_code] = language.language_id;
          let statuses = language.statuses;
          if (typeof statuses === 'string') {
            try {
              statuses = JSON.parse(statuses);
            } catch {
              statuses = {};
            }
          }

          Object.entries(statuses || {}).forEach(([statusKey, template]) => {
            if (!parsed[language.language_code][statusKey]) return;
            parsed[language.language_code][statusKey] = {
              title: template.title || '',
              body: template.body || '',
              enabled: Boolean(template.enabled),
            };
            statusMap[statusKey] = template.status_id;
          });
        });

        languageIds.current = languageMap;
        statusIds.current = statusMap;
        setTemplates(parsed);
        setChangedTemplates([]);
      } catch (loadError) {
        setError(loadError.message || t('Ошибка загрузки push шаблонов'));
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [t, user]);

  if (!user || user.role !== 'admin') {
    return (
      <div style={{ color: 'red', margin: '32px 0', fontWeight: 600, fontSize: '1.2rem' }}>
        Доступ запрещён. Только для администратора.
      </div>
    );
  }

  const trackChange = (languageCode, statusKey, template) => {
    const languageId = languageIds.current[languageCode];
    const statusId = statusIds.current[statusKey];
    if (!languageId || !statusId) return;

    const changed = {
      language_id: languageId,
      status_id: statusId,
      title: template.title,
      body: template.body,
      enabled: template.enabled,
    };
    setChangedTemplates((current) => {
      const index = current.findIndex(
        (item) => item.language_id === languageId && item.status_id === statusId
      );
      if (index === -1) {
        return [...current, changed];
      }
      const next = [...current];
      next[index] = changed;
      return next;
    });
  };

  const handleFieldChange = (languageCode, statusKey, field, value) => {
    const nextTemplate = {
      ...templates[languageCode][statusKey],
      [field]: value,
    };
    setTemplates((current) => ({
      ...current,
      [languageCode]: {
        ...current[languageCode],
        [statusKey]: nextTemplate,
      },
    }));
    trackChange(languageCode, statusKey, nextTemplate);
  };

  const handleEnabledChange = (languageCode, statusKey, enabled) => {
    const nextTemplate = {
      ...templates[languageCode][statusKey],
      enabled,
    };
    setTemplates((current) => ({
      ...current,
      [languageCode]: {
        ...current[languageCode],
        [statusKey]: nextTemplate,
      },
    }));
    trackChange(languageCode, statusKey, nextTemplate);
  };

  const handleSave = async () => {
    if (!changedTemplates.length) {
      setSnackbar({
        open: true,
        message: t('Нет изменений для сохранения'),
        severity: 'info',
      });
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/push-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify(changedTemplates),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || t('Ошибка сохранения push шаблонов'));
      }

      setChangedTemplates([]);
      setSnackbar({
        open: true,
        message: t('Push шаблоны успешно сохранены!'),
        severity: 'success',
      });
    } catch (saveError) {
      setSnackbar({
        open: true,
        message: saveError.message || t('Ошибка сохранения push шаблонов'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const currentLanguage = NOTIFICATION_LANGUAGES[tab].code;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        {t('Push шаблоны')}
      </Typography>
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
          {NOTIFICATION_LANGUAGES.map((language) => (
            <Tab key={language.code} label={language.label} />
          ))}
        </Tabs>

        {NOTIFICATION_STATUSES.map((status, index) => {
          const template = templates[currentLanguage][status.key];
          return (
            <Box
              key={status.key}
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: index !== NOTIFICATION_STATUSES.length - 1 ? '1px solid #eee' : 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2">{status.label}</Typography>
                <TextField
                  label={t('Заголовок')}
                  value={template.title}
                  onChange={(event) => handleFieldChange(currentLanguage, status.key, 'title', event.target.value)}
                  inputProps={{ maxLength: 255 }}
                  fullWidth
                  disabled={!template.enabled || loading}
                />
                <TextField
                  label={t('Текст')}
                  value={template.body}
                  onChange={(event) => handleFieldChange(currentLanguage, status.key, 'body', event.target.value)}
                  helperText={status.key === 'reward' ? `Плейсхолдеры: ${REWARD_PLACEHOLDERS}` : undefined}
                  inputProps={{ maxLength: 2048 }}
                  multiline
                  minRows={3}
                  fullWidth
                  disabled={!template.enabled || loading}
                />
              </Box>
              <FormControlLabel
                sx={{ ml: 3, minWidth: 120, flex: '0 0 auto', justifyContent: 'flex-end' }}
                control={
                  <Switch
                    checked={template.enabled}
                    onChange={(event) => handleEnabledChange(currentLanguage, status.key, event.target.checked)}
                    disabled={loading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#111' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#111',
                      },
                    }}
                  />
                }
                label={template.enabled ? t('Вкл') : t('Выкл')}
                labelPlacement="start"
              />
            </Box>
          );
        })}
      </Paper>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading}
        sx={{ fontWeight: 600, minWidth: 180 }}
      >
        {t('Сохранить')}
      </Button>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
